import { Fragment, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { MailSearch, Search, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react'

interface EmailLog {
  id: number
  recipient: string
  subject: string | null
  template_key: string | null
  channel: string
  status: 'pending' | 'sent' | 'failed'
  is_test: boolean
  error_message: string | null
  provider_error_detail: string | null
  retryable: boolean
  agent_id: number | null
  agent_name: string | null
  created_at: string
}

interface Props extends PageProps {
  logs: EmailLog[]
  counts: { all: number; sent: number; failed: number; pending: number; test: number; real: number }
  filters: { status: string | null; search: string | null; source: string | null }
  pagination: { page: number; per_page: number; total: number; total_pages: number }
}

const statusStyles: Record<string, string> = {
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-gray-100 text-gray-700'
}

const StatusIcon = ({ status }: { status: string }) => {
  if (status === 'sent') return <CheckCircle className="h-3 w-3 inline mr-1" />
  if (status === 'failed') return <XCircle className="h-3 w-3 inline mr-1" />
  return <Clock className="h-3 w-3 inline mr-1" />
}

const humanizeTemplate = (key: string | null) => {
  if (!key) return 'Message'
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function EmailActivityIndex({ logs, counts, filters, pagination }: Props) {
  const [search, setSearch] = useState(filters.search ?? '')
  const [expanded, setExpanded] = useState<number | null>(null)
  const [retrying, setRetrying] = useState<number | null>(null)

  const retry = (id: number) => {
    setRetrying(id)
    router.post(`/admin/email_activity/${id}/retry`, {}, {
      preserveScroll: true,
      onFinish: () => setRetrying(null)
    })
  }

  const navigate = (next: { status?: string | null; search?: string | null; source?: string | null; page?: number }) => {
    const merged = {
      status: next.status !== undefined ? next.status : filters.status,
      search: next.search !== undefined ? next.search : filters.search,
      source: next.source !== undefined ? next.source : filters.source,
      page: next.page ?? 1
    }
    const params: Record<string, string> = {}
    if (merged.status) params.status = merged.status
    if (merged.search) params.search = merged.search
    if (merged.source) params.source = merged.source
    if (merged.page && merged.page > 1) params.page = String(merged.page)
    router.get('/admin/email_activity', params, { preserveState: false })
  }

  const tabs: { id: string | null; label: string; count: number }[] = [
    { id: null, label: 'All', count: counts.all },
    { id: 'sent', label: 'Sent', count: counts.sent },
    { id: 'failed', label: 'Failed', count: counts.failed },
    { id: 'pending', label: 'Pending', count: counts.pending }
  ]

  const sourceTabs: { id: string | null; label: string; count: number }[] = [
    { id: null, label: 'All sources', count: counts.all },
    { id: 'real', label: 'Real sends', count: counts.real },
    { id: 'test', label: 'Test sends', count: counts.test }
  ]

  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.per_page + 1
  const to = Math.min(pagination.page * pagination.per_page, pagination.total)

  return (
    <AppLayout>
      <Head title="Email Activity" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading flex items-center gap-3">
            <MailSearch className="h-7 w-7 text-chestnut-forest" />
            Email Activity
          </h1>
          <p className="text-chestnut-charcoal/70 mt-1">
            Every email the system attempted to send, newest first — including the provider's reason for any failure.
          </p>
        </div>

        <div className="card p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const active = (filters.status ?? null) === tab.id
                return (
                  <button
                    key={tab.label}
                    onClick={() => navigate({ status: tab.id })}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      active
                        ? 'bg-chestnut-forest text-white border-chestnut-forest'
                        : 'bg-white text-chestnut-charcoal/70 border-chestnut-charcoal/15 hover:border-chestnut-forest/50'
                    }`}
                  >
                    {tab.label} <span className="opacity-70">({tab.count})</span>
                  </button>
                )
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              {sourceTabs.map((tab) => {
                const active = (filters.source ?? null) === tab.id
                return (
                  <button
                    key={tab.label}
                    onClick={() => navigate({ source: tab.id })}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      active
                        ? 'bg-chestnut-charcoal text-white border-chestnut-charcoal'
                        : 'bg-white text-chestnut-charcoal/70 border-chestnut-charcoal/15 hover:border-chestnut-charcoal/50'
                    }`}
                  >
                    {tab.label} <span className="opacity-70">({tab.count})</span>
                  </button>
                )
              })}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                navigate({ search: search.trim() || null })
              }}
              className="flex items-center gap-2"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-chestnut-charcoal/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search recipient or subject..."
                  className="pl-9 pr-3 py-2 text-sm border border-chestnut-charcoal/15 rounded-lg w-64 focus:outline-none focus:border-chestnut-forest"
                />
              </div>
              <button type="submit" className="btn-secondary text-sm">Search</button>
              {filters.search && (
                <button
                  type="button"
                  className="text-sm text-chestnut-charcoal/60 hover:text-chestnut-charcoal"
                  onClick={() => {
                    setSearch('')
                    navigate({ search: null })
                  }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-chestnut-charcoal/10">
                <tr className="text-left text-sm text-chestnut-charcoal/60">
                  <th className="pb-3 font-medium">Recipient</th>
                  <th className="pb-3 font-medium">Subject</th>
                  <th className="pb-3 font-medium">Type</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">When</th>
                  <th className="pb-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-chestnut-charcoal/5">
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-sm text-chestnut-charcoal/50">
                      No email activity matches these filters yet.
                    </td>
                  </tr>
                )}
                {logs.map((log) => (
                  <Fragment key={log.id}>
                    <tr>
                      <td className="py-3">
                        <p className="font-medium text-chestnut-charcoal">{log.recipient}</p>
                        {log.agent_name && (
                          <p className="text-xs text-chestnut-charcoal/50">{log.agent_name}</p>
                        )}
                      </td>
                      <td className="py-3 text-sm text-chestnut-charcoal/80 max-w-xs truncate">
                        {log.subject || '—'}
                      </td>
                      <td className="py-3 text-sm text-chestnut-charcoal/70">
                        {humanizeTemplate(log.template_key)}
                        {log.is_test && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            Test
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${statusStyles[log.status]}`}>
                          <StatusIcon status={log.status} />
                          {log.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-chestnut-charcoal/70 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 text-sm">
                        <div className="flex items-center gap-3 whitespace-nowrap">
                          {log.error_message ? (
                            <button
                              onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                              className="text-red-600 hover:underline"
                            >
                              {expanded === log.id ? 'Hide error' : 'View error'}
                            </button>
                          ) : (
                            <span className="text-chestnut-charcoal/40">—</span>
                          )}
                          {log.retryable && (
                            <button
                              onClick={() => retry(log.id)}
                              disabled={retrying !== null}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-chestnut-forest/40 text-chestnut-forest hover:bg-chestnut-forest hover:text-white transition-colors disabled:opacity-50"
                            >
                              <RotateCw className={`h-3 w-3 ${retrying === log.id ? 'animate-spin' : ''}`} />
                              {retrying === log.id ? 'Retrying…' : 'Retry'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === log.id && log.error_message && (
                      <tr>
                        <td colSpan={6} className="pb-4">
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                              {log.status === 'failed' ? 'Provider error' : 'Unconfirmed attempt'}
                            </p>
                            <p className="text-sm text-red-800 break-all">{log.error_message}</p>
                            {log.provider_error_detail && log.provider_error_detail !== log.error_message && (
                              <pre className="text-xs text-red-700/80 whitespace-pre-wrap break-all">{log.provider_error_detail}</pre>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-2 text-sm text-chestnut-charcoal/60">
            <span>
              {pagination.total === 0
                ? 'No records'
                : `Showing ${from}–${to} of ${pagination.total}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => navigate({ page: pagination.page - 1 })}
                className="p-1.5 rounded border border-chestnut-charcoal/15 disabled:opacity-40 hover:border-chestnut-forest/50"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span>
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => navigate({ page: pagination.page + 1 })}
                className="p-1.5 rounded border border-chestnut-charcoal/15 disabled:opacity-40 hover:border-chestnut-forest/50"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
